import { useState } from "react";
import { X } from "lucide-react";

/**
 * GifImage — compact GIF thumbnail that preserves aspect ratio and lets the
 * user tap to view it full-size in a lightbox. Responsive on mobile.
 *
 * Props:
 *   src        — full GIF url
 *   alt        — alt text
 *   maxHeight  — thumbnail cap in px (feed/comments ~200, chat ~260)
 *   testId     — data-testid for the thumbnail <img>
 *   className  — extra classes for the thumbnail wrapper
 */
export default function GifImage({ src, alt = "GIF", maxHeight = 200, testId, className = "" }) {
  const [open, setOpen] = useState(false);
  if (!src) return null;

  return (
    <>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        data-testid={testId}
        className={`block w-auto max-w-full h-auto rounded-2xl border border-white/10 cursor-zoom-in ${className}`}
        style={{ maxHeight: `${maxHeight}px` }}
      />
      {open && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); }}
          data-testid={testId ? `${testId}-lightbox` : "gif-lightbox"}
        >
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
            aria-label="Close"
            data-testid="gif-lightbox-close"
          >
            <X size={20} />
          </button>
          <img
            src={src}
            alt={alt}
            onClick={(e) => e.stopPropagation()}
            className="max-w-[92vw] max-h-[85vh] w-auto h-auto rounded-2xl border border-white/10"
          />
        </div>
      )}
    </>
  );
}
